import { Injectable } from '@angular/core';
import { BrowserAgent } from '@newrelic/browser-agent/loaders/browser-agent';
import { getNewRelicAgent} from '../../../../newrelic';
import { CammsClientStorageService } from 'projects/camms-client-storage/src/public-api';
import { environment } from '@environments/environment';
import { appCodes } from '@core/constants/app-codes.constant';

@Injectable({
  providedIn: 'root',
})
export class NewRelicService {

  private agent: BrowserAgent | null = null;
  private sessionId?: string;
  userId!: string;
  private isDevMode = !environment.production;

  constructor(private storageService: CammsClientStorageService) {
    this.agent = getNewRelicAgent();
    console.log('Developer Mode:', this.isDevMode);
  }


  // ---------- Agent Status ----------
  isNewRelicEnabled(): boolean {
    if (!this.agent) {
      this.agent = getNewRelicAgent();
    }
    const enabled = !!this.agent;
    return enabled;
  }

  getAgent(): BrowserAgent | null {
    if (!this.agent) {
      this.agent = getNewRelicAgent();
      if (!this.agent) {
        if(this.isDevMode) {
          console.warn('[NewRelic] getAgent – agent not available. New Relic features will be disabled.');
        }
      } else {
        if(this.isDevMode) {
          console.log('[NewRelic] getAgent – agent obtained');
        }
      }
    }
    return this.agent;
  }

  setAgent(){
    this.agent = getNewRelicAgent();
    if(this.isDevMode) {
      console.log('[NewRelic] setAgent – agent set');
    }
  }

  // ---------- Custom Attributes ----------
  setCustomAttribute(key: string, value: any): void {

    if (!this.agent) {
      return;
    }
    this.agent.setCustomAttribute(key, value);
  }

  // ---------- Page Actions ----------
  addPageAction(name: string, attributes?: Record<string, any>): void {

    if (!this.agent) {
      if(this.isDevMode) {
        console.warn('[NewRelic] addPageAction skipped – agent missing', { name, attributes });
      }
      return;
    }

    if(this.isDevMode) {
      console.log('[NewRelic] addPageAction', { name, attributes });
    }
    this.agent.addPageAction(name, attributes ?? {});
  }

  // ---------- User Identity ----------
  setUserId(): void {

    if (!this.agent) {
      if(this.isDevMode) {
        console.warn('[NewRelic] setUserId skipped – agent missing');
      }
      return;
    }

    if (!this.userId) {
      if(this.isDevMode) {
        console.warn('[NewRelic] setUserId skipped – userId not set');
      }
      return;
    }

    if(this.isDevMode) {
      console.log('[NewRelic] setUserId', this.userId);
    }
    this.agent.setUserId(this.userId);
  }

  // ---------- Error Reporting ----------
  noticeError(error: any, attributes?: Record<string, any>): void {
    if (!this.agent) {
      if(this.isDevMode) {
        console.warn('[NewRelic] noticeError skipped – agent missing', { error, attributes });
      }
      return;
    }

    if(this.isDevMode) {
      console.error('[NewRelic] noticeError', { error, attributes });
    }
    this.agent.noticeError(error, attributes);
  }

  // ---------- Session Handling ----------
  getSessionId(): string {
    if (!this.agent) {
      return 'NOT_AVAILABLE';
    }

    if (this.sessionId) {
      return this.sessionId;
    }

    this.sessionId = this.generateGUID();
    return this.sessionId;
  }

  setUserAttributes(): void {
    this.getAgent(); // Ensure agent is initialized
    if (!this.agent) {
      if(this.isDevMode) {
        console.warn('[NewRelic] setUserAttributes skipped – agent missing');
      }
      return;
    }
  
    const userName = this.storageService.getSanitizedData(appCodes.auth.userName);
    const context = this.storageService.getSanitizedData(appCodes.auth.organization);
    const userId = this.storageService.getSanitizedData(appCodes.auth.userId);
  
    if (userName) {
      this.agent.setCustomAttribute('UserName', userName);
    }
    if (context) {
      this.agent.setCustomAttribute('Context', context);
    }
    if (userId) {
      this.agent.setCustomAttribute('UserId', userId);
      this.agent.setUserId(userId);
    }

    if(this.sessionId) {
      this.agent.setCustomAttribute('sessionId', this.sessionId);
    }
  }

  // ---------- Utilities ----------
  private generateGUID(): string {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, char => {
      const rand = Math.random() * 16 | 0;
      const value = char === 'x' ? rand : (rand & 0x3 | 0x8);
      return value.toString(16);
    });
  }
}
